import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, Form, FormInput, FormInputTypes, IWarning } from '@praktio/common-ui';
import { Helmet } from 'react-helmet';

import UnauthenticatedWrapper from '../common/UnauthenticatedWrapper/UnauthenticatedWrapper';
import ShowHidePasswordButton from '../common/ShowHidePasswordButton/ShowHidePasswordButton';
import { loginBasic } from '../../ducks/auth';
import { RootState } from '../../ducks/store';
import {
  ErrorCodes,
  InvalidTokenWarning,
  LogoutTypes,
  PlatformMessageTypes,
  GenericFormWarning,
} from '../../utils/constants';
import { useQuery } from '../../utils/functions';

import './Login.scss';

const ComponentId = 'LOGIN';

interface IErrors {
  [key: string]: string | undefined;
}

const renderNotice = (logoutType?: ValueOf<ILogoutTypes>) => {
  switch (logoutType) {
    case LogoutTypes.Inactivity:
      return (
        <div className="alert alert-notice" role="alert">
          <div className="alert-title">Your session has timed out.</div>
          <div className="alert-body">
            There was no activity for a while, so we logged you out. Don't worry—if you were in the
            middle of a course, we saved your progress.
          </div>
        </div>
      );
    case LogoutTypes.Maintenance:
      return (
        <div className="alert alert-notice" role="alert">
          <div className="alert-title">Your session timed out for scheduled maintenance.</div>
          <div className="alert-body">
            Scheduled maintenance began, so we logged you out. Don't worry—if you were in the middle
            of a course, we saved your progress.
          </div>
        </div>
      );
    default:
      return;
  }
};

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = useQuery();
  const invalidTokenError = query.get('tokenError') === '1';
  const from = (location as any)?.state?.from?.pathname || '/dashboard';
  const [form, setForm] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<IErrors>();
  const [passwordFieldType, setPasswordFieldType] = useState<FormInputTypes>('password');
  const [warnings, setWarnings] = useState<IWarning[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMounted = useRef(false);

  const dispatch = useDispatch();
  const { isAuthenticated, logoutType, messages } = useSelector((state: RootState) => {
    const { isAuthenticated, logoutType } = state.auth;
    return {
      isAuthenticated,
      logoutType,
      messages: state.platformData.messages.filter((m) => m.componentId?.includes(ComponentId)),
    };
  });

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  if (isAuthenticated) {
    return <Navigate to={from} />;
  }

  const togglePassword = () => {
    setPasswordFieldType(passwordFieldType === 'password' ? 'text' : 'password');
  };

  const setField = (field: string, value: string) => {
    setForm({
      ...form,
      [field]: value,
    });
    if (errors && !!errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }
  };

  const findFormErrors = () => {
    const { email, password } = form;
    const newErrors: IErrors = {};
    // Email Errors
    if (!email || email === '') {
      newErrors.email = 'This field cannot be blank.';
    }
    // Password Errors
    if (!password || password === '') {
      newErrors.password = 'This field cannot be blank.';
    }

    return newErrors;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (preventSubmit) {
      return;
    }
    if (!isSubmitting) {
      setIsSubmitting(true);
      const newErrors = findFormErrors();
      const newWarnings = [];
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
      } else {
        try {
          const email = form.email;
          const password = form.password;
          await dispatch(loginBasic({ email, password }));
        } catch (e) {
          const error = e as IError;
          const { errorCode } = error;
          const alert =
            messages.find(
              (m) =>
                m.type === PlatformMessageTypes.Alert &&
                m.componentId?.includes(ComponentId) &&
                m.errorCode === errorCode,
            ) || GenericFormWarning;
          switch (errorCode) {
            case ErrorCodes.InvalidPassword:
            case ErrorCodes.InvalidEmail:
              newWarnings.push(alert);
              break;
            case ErrorCodes.ExpiredPassword:
              navigate(`/forgot?expired=1`);
              break;
            case ErrorCodes.MaintenanceMode:
              navigate(`/maintenance`);
              break;
            default:
              break;
          }
        } finally {
          if (isMounted.current) {
            setIsSubmitting(false);
          }
        }
      }

      if (isMounted.current) {
        setWarnings(newWarnings);
      }
    }
  };

  const preventSubmit = isSubmitting || form.email.length < 1 || form.password.length < 1;

  return (
    <UnauthenticatedWrapper formName="Log in to your account">
      <Helmet>
        <title>Login - Praktio</title>
      </Helmet>
      {renderNotice(logoutType)}
      {warnings &&
        warnings.length > 0 &&
        warnings.map((warning, i) => (
          <div key={`warning-${i}`} className="alert alert-danger" role="alert">
            <div className="alert-title">{warning.title}</div>
            <div className="alert-body">{warning.body}</div>
          </div>
        ))}
      {!warnings?.length && invalidTokenError && (
        <div key={`warning-token`} className="alert alert-warning" role="alert">
          <div className="alert-title">{InvalidTokenWarning.title}</div>
          <div className="alert-body">{InvalidTokenWarning.body}</div>
        </div>
      )}
      <Form
        formId="login-form"
        className="login-form"
        onSubmit={(e: any) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <FormInput
          formGroupClassName="LoginEmail"
          value={form.email}
          fieldName="email"
          label="Email"
          placeholder="Email"
          onChange={(e: any) => setField('email', e.target.value.replaceAll('’', `'`))}
          error={errors?.email}
        />
        <FormInput
          formGroupClassName="LoginPassword"
          value={form.password}
          fieldName="password"
          label="Password"
          placeholder="Password"
          type={passwordFieldType}
          onChange={(e: any) => setField('password', e.target.value)}
          error={errors?.password}
          labelWrapperClassName="label-show-hide-group d-flex justify-content-between"
          labelAdditions={
            <ShowHidePasswordButton
              clickFunction={togglePassword}
              passwordVisible={passwordFieldType === 'text'}
              passwordFieldType={passwordFieldType}
            />
          }
          lastInSection
          noTrimming
        />
        <div className="forgot-password mt-1 mb-3">
          <Link to="/forgot">Forgot password?</Link>
        </div>
        <div className="login-button-wrapper">
          <Button
            form="login-form"
            type="submit"
            btnStyle="primary"
            disabled={preventSubmit}
            showSpinner={isSubmitting}
            overlaySpinner
            onClick={(e: any) => handleSubmit(e)}
          >
            LOG IN
          </Button>
        </div>
        <div className="sso-login">
          <Button
            onClick={() => {
              navigate(`/sso`);
            }}
            className="sso-link"
            noStyling
          >
            <span>Log in with SSO</span>
          </Button>
        </div>
        <div className="form-footer">
          By logging in, you agree to Praktio's{' '}
          <a href="https://praktio.com/pages/terms-of-service">Terms of Service</a>, along with our{' '}
          <a href="https://praktio.com/pages/privacy-policy">Privacy Policy</a>. Our site uses
          cookies, as further detailed in our Privacy Policy, and you consent to such use by logging
          in.
        </div>
      </Form>
    </UnauthenticatedWrapper>
  );
};

export default Login;
